import React, { useState, useMemo } from "react";
import { VStack, Box, Text, Button } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AgendaLoader } from "@components";
import SingleActivityCard from "../Activity/ActivityCard";
import ActivityAccordion from "./ActivityAccordion";
import { AnnouncementIcon, CheckboxIcon, ClockIcon } from "./AccordionIcons";
import { categorizeActivities } from "@utils/activityHelpers";
import { $Routes } from "@routes";

const ActivityList = ({
  agendaId,
  currentAgenda,
  isLoading,
  onEditActivity,
  isPastAgenda = false,
}) => {
  const navigate = useNavigate();
  const activities = useSelector((state) => state.activity.activities);
  const gracePeriodActivities = useSelector(
    (state) => state.activity.gracePeriodActivities
  );

  const isDemoActive = useSelector((state) => state.demo.isActive);

  // Single state to manage which accordion is expanded
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  // Single state to manage which activity detail is expanded
  const [expandedActivityId, setExpandedActivityId] = useState(null);

  // Helper functions to check if each accordion is open
  const isAnnouncementsOpen = expandedAccordion === "announcements";
  const isCompletedOpen = expandedAccordion === "completed";
  const isUnrunOpen = expandedAccordion === "unrun";

  // Toggle functions that ensure only one accordion is open at a time
  const toggleAnnouncements = () => {
    setExpandedAccordion(isAnnouncementsOpen ? null : "announcements");
  };

  const toggleCompleted = () => {
    setExpandedAccordion(isCompletedOpen ? null : "completed");
  };

  const toggleUnrun = () => {
    setExpandedAccordion(isUnrunOpen ? null : "unrun");
  };

  // Function to handle activity detail toggle
  const handleActivityDetailToggle = (activityId) => {
    setExpandedActivityId(
      expandedActivityId === activityId ? null : activityId
    );
  };

  const categorizedActivities = useMemo(() => {
    if (!activities || activities.length === 0) {
      return { pending: [], announcementsSent: [], completed: [], unrun: [] };
    }
    return categorizeActivities(
      activities,
      isPastAgenda,
      gracePeriodActivities,
      { keepCompletedInPlace: isDemoActive }
    );
  }, [activities, isPastAgenda, gracePeriodActivities, isDemoActive]);

  if (isLoading) {
    return <AgendaLoader />;
  }

  const hasActivities = activities?.length > 0;
  const { pending, announcementsSent, completed, unrun } =
    categorizedActivities;

  if (!hasActivities) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="granite" fontSize="sm">
          No activities in this agenda yet
        </Text>
        {!isPastAgenda && (
          <Button
            size="sm"
            variant="ghost"
            color="purple.500"
            mt={2}
            onClick={() => navigate(`${$Routes.agendaPage}/${agendaId}`)}
          >
            Add Activities
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box height="100%" overflowY="auto">
      <VStack spacing={3} align="stretch">
        {/* Pending Activities - Only show for live agendas */}
        {!isPastAgenda &&
          pending.map((activity, index) => (
            <SingleActivityCard
              key={activity._id || `pending-${index}`}
              activity={activity}
              agenda={currentAgenda}
              index={index}
              simplified={true}
              changeViewData={{
                views: { add: "add" },
                setView: () => navigate(`${$Routes.agendaPage}/${agendaId}`),
              }}
              setActivityType={() => {}}
              onEditActivity={onEditActivity}
              isAccordionOpen={expandedActivityId === activity._id}
              onToggleAccordion={() => handleActivityDetailToggle(activity._id)}
            />
          ))}

        {/* Announcements Sent Accordion */}
        {announcementsSent.length > 0 && (
          <ActivityAccordion
            title="Announcements Sent"
            count={announcementsSent.length}
            icon={AnnouncementIcon}
            isOpen={isAnnouncementsOpen}
            onToggle={toggleAnnouncements}
          >
            <VStack spacing={3} align="stretch" mt={2}>
              {announcementsSent.map((activity, index) => (
                <SingleActivityCard
                  key={activity._id || `announcement-${index}`}
                  activity={activity}
                  agenda={currentAgenda}
                  index={index}
                  simplified={true}
                  changeViewData={{
                    views: { add: "add" },
                    setView: () =>
                      navigate(`${$Routes.agendaPage}/${agendaId}`),
                  }}
                  setActivityType={() => {}}
                  onEditActivity={onEditActivity}
                  isAccordionOpen={expandedActivityId === activity._id}
                  onToggleAccordion={() =>
                    handleActivityDetailToggle(activity._id)
                  }
                />
              ))}
            </VStack>
          </ActivityAccordion>
        )}

        {/* Completed Activities Accordion */}
        {completed.length > 0 && (
          <ActivityAccordion
            title="Completed Activities"
            count={completed.length}
            icon={CheckboxIcon}
            isOpen={isCompletedOpen}
            onToggle={toggleCompleted}
          >
            <VStack spacing={3} align="stretch" mt={2}>
              {completed.map((activity, index) => (
                <SingleActivityCard
                  key={activity._id || `completed-${index}`}
                  activity={activity}
                  agenda={currentAgenda}
                  index={index}
                  simplified={true}
                  changeViewData={{
                    views: { add: "add" },
                    setView: () =>
                      navigate(`${$Routes.agendaPage}/${agendaId}`),
                  }}
                  setActivityType={() => {}}
                  onEditActivity={onEditActivity}
                  isAccordionOpen={expandedActivityId === activity._id}
                  onToggleAccordion={() =>
                    handleActivityDetailToggle(activity._id)
                  }
                />
              ))}
            </VStack>
          </ActivityAccordion>
        )}

        {/* Unrun Activities Accordion - Only show for past agendas */}
        {isPastAgenda && unrun.length > 0 && (
          <ActivityAccordion
            title="Unrun Activities"
            count={unrun.length}
            icon={ClockIcon}
            isOpen={isUnrunOpen}
            onToggle={toggleUnrun}
          >
            <VStack spacing={3} align="stretch" mt={2}>
              {unrun.map((activity, index) => (
                <SingleActivityCard
                  key={activity._id || `unrun-${index}`}
                  activity={activity}
                  agenda={currentAgenda}
                  index={index}
                  simplified={true}
                  changeViewData={{
                    views: { add: "add" },
                    setView: () =>
                      navigate(`${$Routes.agendaPage}/${agendaId}`),
                  }}
                  setActivityType={() => {}}
                  onEditActivity={onEditActivity}
                  isAccordionOpen={expandedActivityId === activity._id}
                  onToggleAccordion={() =>
                    handleActivityDetailToggle(activity._id)
                  }
                />
              ))}
            </VStack>
          </ActivityAccordion>
        )}
      </VStack>
    </Box>
  );
};

export default ActivityList;
